import { useEffect, useState } from 'react';

interface Step {
  number: number;
  icon: string;
  title: string;
  description: string;
  color: string;
}

const steps: Step[] = [
  {
    number: 1,
    icon: 'ri-chat-3-line',
    title: 'Consultation',
    description: 'Tell us your needs',
    color: 'from-[#004aad] to-[#0066dd]'
  },
  {
    number: 2,
    icon: 'ri-file-list-3-line',
    title: 'Custom Plan',
    description: 'We design a solution',
    color: 'from-[#ffbd59] to-[#ffa726]'
  },
  {
    number: 3,
    icon: 'ri-rocket-line',
    title: 'Execution',
    description: 'Our team gets to work',
    color: 'from-[#004aad] to-[#0066dd]'
  },
  {
    number: 4,
    icon: 'ri-line-chart-line',
    title: 'Growth',
    description: 'You see results',
    color: 'from-[#ffbd59] to-[#ffa726]'
  }
];

export default function HowItWorks() {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById('how-it-works');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % steps.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  return (
    <section id="how-it-works" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <h2 className="text-5xl font-bold text-gray-900 mb-4">How It Works</h2>
          <p className="text-xl text-gray-600">Simple, transparent, and results-driven process</p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Connection Line */}
          <div className="absolute top-16 left-0 right-0 h-1 bg-gray-200 hidden lg:block">
            <div 
              className="h-full bg-gradient-to-r from-[#004aad] via-[#ffbd59] via-[#004aad] to-[#ffbd59] transition-all duration-1000"
              style={{ width: isVisible ? '100%' : '0%' }}
            ></div>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`relative transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                {/* Node */}
                <div className="flex justify-center mb-8">
                  <div className={`relative w-32 h-32 rounded-full bg-white shadow-xl flex items-center justify-center border-4 transition-all duration-300 ${
                    activeStep === index ? 'border-[#ffbd59] scale-110' : 'border-gray-200'
                  }`}>
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center`}>
                      <i className={`${step.icon} text-3xl text-white`}></i>
                    </div>
                    {activeStep === index && (
                      <div className="absolute inset-0 rounded-full border-4 border-[#ffbd59] animate-ping"></div>
                    )}
                  </div>
                </div>

                {/* Card */}
                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 relative">
                  <div className={`absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center text-white text-3xl font-bold opacity-20`}>
                    {step.number}
                  </div>
                  <h3 className={`text-2xl font-bold mb-3 bg-gradient-to-r ${step.color} bg-clip-text text-transparent`}>
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Progress Indicators */}
        <div className="flex justify-center gap-3 mt-12 lg:hidden">
          {steps.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveStep(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                activeStep === index ? 'bg-[#ffbd59] w-8' : 'bg-gray-300'
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}